<template>
  <div class="flex">
    <AppSidebar
      ref="appSidebar"
      :model="menu"
      class="overflow-hidden rounded-r-xl transition-shadow lg:rounded-r-none"
      :class="{
        'shrink-0': lgAndLarger,
        '!fixed left-0 top-0': !lgAndLarger,
        'shadow-2xl': isMobileMenuVisible,
      }"
    >
      <template
        v-if="$slots['app-sidebar-header']"
        #header
      >
        <slot name="app-sidebar-header" />
      </template>

      <template
        v-if="$slots['app-sidebar-body']"
        #body
      >
        <slot name="app-sidebar-body" />
      </template>

      <template
        v-if="$slots['app-sidebar-footer']"
        #footer
      >
        <slot name="app-sidebar-footer" />
      </template>
    </AppSidebar>

    <div
      v-if="isMobileMenuVisible"
      ref="mobileOverlay"
      class="fixed inset-0 z-50 cursor-pointer bg-black/20 dark:bg-white/10"
      @click="layoutStore.closeMobileMenu()"
    />

    <slot />
  </div>
</template>

<script lang="ts" setup>
import { useMotion } from '@vueuse/motion'
import AppSidebar from '~/components/layouts/default/AppSidebar.vue'
import { lgAndLarger } from '~/composables/useUI'
import { useLayoutStore } from '~/store/layout'
import type { MenuItem } from '~types/app'

defineProps<{
  menu: MenuItem[]
}>()

const layoutStore = useLayoutStore()
const { isMobileMenuVisible } = storeToRefs(layoutStore)

const route = useRoute()
const toast = usePrimevueToast()
watch(() => route.fullPath, () => {
  toast.removeAllGroups()
  if (isMobileMenuVisible.value) layoutStore.closeMobileMenu()
})

watch(lgAndLarger, (isLarger) => {
  if (isLarger) {
    expandSidebar()
    if (isMobileMenuVisible.value) layoutStore.closeMobileMenu()
  }
  else {
    collapseSidebar()
  }
})

const appSidebar = useTemplateRef('appSidebar')
const mobileOverlay = useTemplateRef('mobileOverlay')
const { apply: applyAppSidebar } = useMotion(appSidebar, {
  initial: { translateX: lgAndLarger.value ? '0' : '-100%' },
})
const { apply: applyMobileOverlay } = useMotion(mobileOverlay, {
  initial: { opacity: 0 },
})
const collapseSidebar = async () => {
  await applyAppSidebar({ translateX: '-100%' })
  await applyMobileOverlay({ opacity: 0 })
}
const expandSidebar = async () => {
  await applyAppSidebar({ translateX: '0' })
  await applyMobileOverlay({ opacity: 1 })
}
watch(isMobileMenuVisible, (isVisible) => {
  if (lgAndLarger.value) return

  if (isVisible) expandSidebar()
  else collapseSidebar()
})
</script>
